"use client"

import type { FooterSectionData } from "./footer.data"

import { useContentVisibility } from "@/helpers/Providers"
import clsx from "clsx"
import Link from "next/link"
import { usePathname } from "next/navigation"

import GitHubIcon from "../icons/GitHubIcon"
import LinkedInIcon from "../icons/LinkedInIcon"
import MailIcon from "../icons/MailIcon"
import XIcon from "../icons/XIcon"
import { YoutubeIcon } from "../icons/YoutubeIcon"

function socialIcon(url: string) {
  let parsed
  try {
    parsed = new URL(url, "https://www.dash0.com")
  } catch {
    return null
  }
  const hostname = parsed.hostname
  if (hostname.endsWith("github.com")) return <GitHubIcon />
  if (hostname.endsWith("linkedin.com")) return <LinkedInIcon />
  if (hostname.endsWith("x.com") || hostname.endsWith("twitter.com"))
    return <XIcon />
  if (hostname.endsWith("youtube.com")) return <YoutubeIcon />
  if (hostname.endsWith("dash0.com") && parsed.pathname === "/contact")
    return <MailIcon />
  return null
}

function isJobBoardLink(url: string) {
  try {
    return (
      new URL(url, "https://www.dash0.com").hostname === "jobs.ashbyhq.com"
    )
  } catch {
    return false
  }
}

export default function FooterSections({
  sections,
  openPositions,
  className,
}: { sections: FooterSectionData[]; openPositions: number | null; className?: string }) {
  const { hide } = useContentVisibility()
  const pathName = usePathname();

  const isLandingPage = pathName.startsWith("/lp/")

  if (isLandingPage) {
    return null
  }

  return (
    <div
      className={clsx(
        "grid grid-cols-2 lg:grid-cols-3 gap-x-6 lg:gap-y-16 gap-y-8",
        className
      )}
    >
      {sections?.map((section) => {
        const isWide = section.links?.length >= 6

        return (
          <div key={section.title} className={clsx(isWide && "max-lg:col-span-2")}>
            <p className="font-semibold  text-white/70 text-[11px] tracking-[8%] uppercase mb-4 leading-none">
              {section.title}
            </p>
            <ul
              className={clsx(
                isWide
                  ? "grid grid-cols-2 gap-x-6 content-start"
                  : "flex flex-col"
              )}
            >
              {section.links.map((link) => {
                if (!link.url) return null
                const icon = socialIcon(link.url)

                return (
                  <li key={link.title}>
                    <Link
                      prefetch={false}
                      href={link.url}
                      onClick={() => {
                        hide(link.url)
                      }}
                      target={link.isExternal ? "_blank" : "_self"}
                      className="lg:font-medium flex items-center gap-x-3 w-fit text-base first-of-type:pt-0 py-1 transition-opacity duration-100 hover:opacity-70 [&_svg]:size-[17px] [&_svg]:shrink-0"
                    >
                      {icon}
                      {link.title}
                      {openPositions != null && isJobBoardLink(link.url) && (
                        <>
                          {' '}
                          <span 
                            aria-label={`${openPositions} open positions`}
                            className="text-xs font-semibold text-white/70 bg-grey-850 rounded-full px-2 py-0.5 leading-none">
                            {openPositions}
                          </span>
                        </>
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
